"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader } from "lucide-react";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-2 border-primary/50 bg-primary/10 p-8 text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-xl font-serif font-bold text-primary mb-4">
          Besked Modtaget
        </h3>
        <p className="text-foreground mb-6 font-mono text-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-wider">
          Send ny besked
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Navn er påkrævet"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
              Dit navn
            </FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="rounded-none border-border bg-background/50 focus-visible:ring-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-mail er påkrævet",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ugyldig e-mailadresse"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
              E-mailadresse
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="rounded-none border-border bg-background/50 focus-visible:ring-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
              Emne
            </FormLabel>
            <FormControl>
              <Input placeholder="Kort beskrivelse af din henvendelse" {...field} className="rounded-none border-border bg-background/50 focus-visible:ring-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Besked er påkrævet"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono uppercase text-xs tracking-widest text-muted-foreground">
              Din besked
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Skriv din besked her..." {...field} className="min-h-[200px] rounded-none border-2 border-muted-foreground/40 bg-background/50 focus-visible:border-primary focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subscribe" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-2 border border-border/50 bg-card/30">
            <FormControl>
              <input type="checkbox" checked={value} onChange={onChange} className="size-4 mt-1 border-primary text-primary focus:ring-primary rounded-none cursor-pointer accent-primary" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-mono uppercase text-xs tracking-wider text-muted-foreground cursor-pointer">
                Modtag nyhedsbrev
              </FormLabel>
              <p className="text-[10px] text-muted-foreground/70">
                Tilmeld dig vores nyhedsbrev for opdateringer.
              </p>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-none border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-[0.2em] font-bold text-lg transition-all duration-300 shadow-[0_0_15px_rgba(var(--primary),0.1)] hover:shadow-[0_0_25px_rgba(var(--primary),0.3)]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sender...
          </> : "Send"}
      </Button>
    </Form>;
}
export default function SendDinBesked() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="send-din-besked" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556341/img/noise_texture.png)] bg-repeat mix-blend-overlay"></div>
      
      <div ref={ref} className={`max-w-lg mx-auto relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold tracking-tight text-primary">
            Send din Besked
          </h2>
          <div className="h-1 w-24 bg-primary mx-auto" />
          <p className="text-muted-foreground font-mono text-sm md:text-base max-w-md mx-auto">
            Har du spørgsmål, eller ønsker du at bidrage til debatten? Skriv direkte til os herunder.
          </p>
        </div>

        <div className="bg-card/20 border border-border p-6 md:p-10 backdrop-blur-sm shadow-2xl">
          <FormProvider formId="contact_form_contact" sectionName="contact-section" defaultValues={{
          name: "",
          email: "",
          subject: "",
          message: "",
          subscribe: false
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          message: {
            required: true
          }
        }} submitText="Send" successMessage="Tak for din besked. Vi vender tilbage hurtigst muligt." errorMessage="Der opstod en fejl. Prøv venligst igen.">
            <ContactForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}